import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/Login";
import EventsList from "@/pages/EventsList";
import EventDetail from "@/pages/EventDetail";
import OfficialsPage from "@/pages/Officials";
import PayrollPage from "@/pages/Payroll";
import SettingsPage from "@/pages/Settings";
import PublicSignup from "@/pages/PublicSignup";
import PublicSchedule from "@/pages/PublicSchedule";
import PublicScheduleShare from "@/pages/PublicScheduleShare";
import UsersAdmin from "@/pages/UsersAdmin";
import OfficialLogin from "@/pages/OfficialLogin";
import OfficialForgotPassword from "@/pages/OfficialForgotPassword";
import OfficialResetPassword from "@/pages/OfficialResetPassword";
import OfficialDashboard from "@/pages/OfficialDashboard";
import Dashboard from "@/pages/Dashboard";
import HelpPage from "@/pages/Help";
import IdleLogout from "@/components/IdleLogout";
import CookieBanner from "@/components/CookieBanner";
import Privacy from "@/pages/Privacy";

function ProtectedRoute({ children, requireRole }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
        <div className="font-mono text-xs uppercase tracking-widest text-[#525252] alert-pulse">Chargement…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "official" && !requireRole) {
    return <Navigate to="/officiel" replace />;
  }
  if (requireRole && user.role !== requireRole) {
    if (user.role === "official") return <Navigate to="/officiel" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

function OfficialOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/officiel/login" replace />;
  if (user.role !== "official") return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <IdleLogout />
        <CookieBanner />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "#FFFFFF",
              border: "1px solid #000",
              borderRadius: 0,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12,
              color: "#0A0A0A",
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/officiel/login" element={<OfficialLogin />} />
          <Route path="/officiel/mot-de-passe-oublie" element={<OfficialForgotPassword />} />
          <Route path="/officiel/reset/:token" element={<OfficialResetPassword />} />
          <Route path="/officiel" element={<OfficialOnly><OfficialDashboard /></OfficialOnly>} />
          <Route path="/inscription/:token" element={<PublicSignup />} />
          <Route path="/horaire/:eventId" element={<PublicSchedule />} />
          <Route path="/share/horaire/:token" element={<PublicScheduleShare />} />
          <Route path="/confidentialite" element={<Privacy />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="evenements" element={<EventsList />} />
            <Route path="evenements/:eventId" element={<EventDetail />} />
            <Route path="officiels" element={<OfficialsPage />} />
            <Route path="paies" element={<PayrollPage />} />
            <Route path="reglages" element={<SettingsPage />} />
            <Route path="aide" element={<HelpPage />} />
            <Route
              path="utilisateurs"
              element={
                <ProtectedRoute requireRole="superadmin">
                  <UsersAdmin />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
